import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, AlertTriangle, Settings, CalendarCheck, ClipboardCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import CFiltersBar from '../components/shared/c-filters-bar';
import { useAuth } from '../contexts/AuthContext';
import { cellClass, monthOptions, EMPLOYMENT_TYPES } from '../utils/attendanceCell';
import AttendanceOverrideModal from '../components/AttendanceOverrideModal';
import AttendancePolicyModal from '../components/AttendancePolicyModal';
import UnlinkedMembersBanner from '../components/UnlinkedMembersBanner';
import AmendmentsQueue from '../components/attendance/AmendmentsQueue';
import WfhQueue from '../components/attendance/WfhQueue';
import ApprovalFilters, { useApprovalFilters } from '../components/attendance/ApprovalFilters';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 8;

/**
 * The matrix query string for a set of filters. The matrix and its export send
 * the same one, so the spreadsheet always matches what is on screen.
 */
const matrixQuery = ({ month, departmentIds, employmentType }, search) => {
  const params = new URLSearchParams({ month });
  if (departmentIds.length) params.set('department_ids', departmentIds.join(','));
  if (employmentType) params.set('employment_type', employmentType);
  if (search) params.set('search', search);
  return params.toString();
};

/**
 * Stored filters from before the department filter took several values carry a
 * single departmentId; read it as a one-item list rather than dropping it.
 */
const normaliseFilters = (stored, defaults) => {
  const { departmentId, ...rest } = stored || {};
  const merged = { ...defaults, ...rest };
  if (!Array.isArray(rest.departmentIds)) {
    merged.departmentIds = departmentId ? [String(departmentId)] : [];
  }
  if (typeof merged.search !== 'string') merged.search = '';
  return merged;
};

const Attendance = () => {
  const { hasPermission } = useAuth();
  const canOverride = hasPermission('attendance', 'override');
  const canManagePolicy = hasPermission('attendance', 'manage_policy');
  // read_own does not reach the matrix endpoint (the API returns 403), so do not
  // render a shell that can only ever be empty. Same panel UserManagement uses.
  const canReadAll = hasPermission('attendance', 'read_all');
  const canViewMatrix = canReadAll || hasPermission('attendance', 'read_department');
  const canViewApprovals = hasPermission('timesheet', 'read_all')
    || hasPermission('timesheet', 'read_department');

  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [];
  if (canViewMatrix) tabs.push({ key: 'attendance', label: 'Attendance', Icon: CalendarCheck });
  if (canViewApprovals) tabs.push({ key: 'approvals', label: 'Approvals', Icon: ClipboardCheck });
  // ?tab= is part of the page's URL contract: /timesheet-approvals redirects onto it.
  // The URL stays the source of truth. The stored tab only answers when the URL
  // carries none, which is what coming back through the sidebar looks like.
  const [storedTab, setStoredTab] = usePersistedState('attendance.tab', '');
  const urlTab = searchParams.get('tab');
  const requestedTab = urlTab || storedTab;
  const openTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab : tabs[0]?.key;
  const showTab = (key) => {
    // Recorded here rather than from openTab: showing the matrix empties the
    // query string, so reading the URL back would just return the stored tab.
    setStoredTab(key);
    setSearchParams(key === 'attendance' ? {} : { tab: key });
  };

  // Arriving on ?tab=approvals is the other way a tab gets chosen.
  useEffect(() => {
    if (urlTab) setStoredTab(urlTab);
  }, [urlTab, setStoredTab]);

  // One row of filters for both approval queues, held here so both read it.
  const approvalFilters = useApprovalFilters({
    canReadAll: hasPermission('timesheet', 'read_all'),
    enabled: openTab === 'approvals',
  });

  const months = useMemo(() => monthOptions(), []);
  const defaultFilters = useMemo(() => ({
    month: months[0].value, departmentIds: [], employmentType: '', search: '',
  }), [months]);
  const [storedFilters, setFilters] = usePersistedState('attendance.filters', defaultFilters);
  const filters = useMemo(
    () => normaliseFilters(storedFilters, defaultFilters),
    [storedFilters, defaultFilters]
  );
  // Typing updates filters.search at once (the field is controlled by it); the
  // request waits for a pause so a name does not cost one fetch per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search.trim());
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // A department-scope reader is pinned to their own department server-side, so
  // the department filter would only ever offer choices that change nothing.
  useEffect(() => {
    if (!canReadAll) return;
    api.get('/api/departments')
      .then((res) => setDepartments(res.data.data || []))
      .catch(() => setDepartments([]));
  }, [canReadAll]);

  // Everything but the search, so the suggestion roster below can tell whether
  // it still describes the rows the other filters select.
  const baseQuery = matrixQuery(filters, '');
  const query = matrixQuery(filters, debouncedSearch);
  const beginRequest = useLatestRequest();
  // Names of everyone the other filters select, from the last unsearched load.
  // Suggestions come from here so they keep offering other names while a search
  // has narrowed the matrix itself.
  const [roster, setRoster] = useState({ query: null, names: [] });

  const load = useCallback(async () => {
    if (!canViewMatrix) { setLoading(false); return; }
    const isCurrent = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/attendance/matrix?${query}`);
      if (!isCurrent()) return;
      setData(res.data.data);
      if (!debouncedSearch) {
        setRoster({ query, names: (res.data.data.rows || []).map((row) => row.name) });
      }
    } catch (err) {
      if (isCurrent()) setError('Could not load attendance. Please try again.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [query, debouncedSearch, canViewMatrix, beginRequest]);

  useEffect(() => { load(); }, [load]);

  const setFilterValue = (key, value) => setFilters((f) => ({ ...normaliseFilters(f, defaultFilters), [key]: value }));
  const setFilter = (key) => (e) => setFilterValue(key, e.target.value);
  const clearFilters = () => {
    setFilters(defaultFilters);
    setDebouncedSearch('');
  };

  const searchText = filters.search.trim().toLowerCase();
  const wantsSuggestions = Boolean(searchText);

  // The roster normally comes for free from an unsearched load. A search that is
  // already active when the other filters change, or one restored from storage
  // on arrival, never produces one, so fetch the unsearched names separately:
  // same filters, same endpoint, so the server applies the same scope.
  useEffect(() => {
    if (!canViewMatrix || !wantsSuggestions || roster.query === baseQuery) return undefined;
    let cancelled = false;
    api.get(`/api/attendance/matrix?${baseQuery}`)
      .then((res) => {
        if (cancelled) return;
        setRoster({ query: baseQuery, names: (res.data.data.rows || []).map((row) => row.name) });
      })
      .catch(() => {}); // Suggestions fall back to the rows on screen.
    return () => { cancelled = true; };
  }, [canViewMatrix, wantsSuggestions, baseQuery, roster.query]);

  const suggestions = useMemo(() => {
    if (!searchText) return [];
    const names = roster.query === baseQuery
      ? roster.names
      : ((data && data.rows) || []).map((row) => row.name);
    return [...new Set(names)]
      .filter((name) => name.toLowerCase().includes(searchText) && name.toLowerCase() !== searchText)
      .slice(0, MAX_SUGGESTIONS);
  }, [searchText, roster, baseQuery, data]);

  const isFiltered = Boolean(
    filters.departmentIds.length || filters.employmentType || debouncedSearch
  );

  const exportMonth = async () => {
    try {
      const res = await api.get(`/api/attendance/export?${query}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-${filters.month}.xlsx`;
      // Must be in the document: a detached anchor's programmatic click() is a
      // no-op in Firefox. Revoke on the next tick so Safari finishes the transfer.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  if (tabs.length === 0) {
    return (
      <div className="w-full mx-auto px-0 py-6">
        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium">Access Denied</h3>
            <p className="text-red-600 mt-1">
              You don't have permission to view this page. Your own attendance is at
              {' '}
              <Link to="/my-attendance" className="underline">My Attendance</Link>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const zone = (data && data.timezone) || null;

  // Hovering a cell is the quickest way to see when someone actually signed in.
  const cellTooltip = (cell) => {
    const parts = [];
    const at = (iso) => new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', timeZone: zone || 'UTC',
    });
    if (cell.first_signin_at) {
      parts.push(cell.declared_start_at
        ? `Posted ${at(cell.first_signin_at)}, announced start ${at(cell.declared_start_at)}${zone ? ` (${zone})` : ''}`
        : `Signed in ${at(cell.first_signin_at)}${zone ? ` (${zone})` : ''}`);
    }
    if (cell.signin_text) parts.push(cell.signin_text);
    return parts.length ? parts.join(' - ') : undefined;
  };

  // Only the first load blanks the matrix. A refetch for a filter change keeps
  // the current rows up, with the spinner in the search field, so typing a name
  // does not flash the grid away on every pause.
  const firstLoad = loading && !data;
  const dates = (data && data.dates) || [];
  const rows = (data && data.rows) || [];

  const pageHeader = (
    <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Attendance</h1>
        </div>
        {openTab === 'attendance' && (
          <div className="flex items-center gap-3">
            {data && data.last_run && (
              <span data-testid="last-run" className="max-w-[16rem] text-right text-xs leading-snug text-gray-500">
                {data.last_run.error
                  ? `Last run failed: ${data.last_run.error}`
                  : `Last updated ${new Date(data.last_run.run_finished_at).toLocaleString()}`}
              </span>
            )}
            <button
              type="button"
              onClick={exportMonth}
              className={'inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white'
                + ' px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Export
            </button>
            {canManagePolicy && (
              <button
                type="button"
                onClick={() => setPolicyOpen(true)}
                data-testid="policy-settings"
                aria-label="Attendance policy settings"
                title="Attendance policy settings"
                className={'inline-flex items-center justify-center rounded-md border border-gray-300'
                  + ' bg-white p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
              >
                <Settings className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>

      {tabs.length > 1 && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                data-testid={`${key}-tab`}
                onClick={() => showTab(key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  openTab === key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="inline h-4 w-4 mr-2" aria-hidden="true" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  );

  // Two stacked queues on one scroll, each with its own heading and count.
  if (openTab === 'approvals') {
    return (
      <div className="space-y-6">
        {pageHeader}
        <ApprovalFilters filters={approvalFilters} />
        <div className="space-y-10">
          <AmendmentsQueue filters={approvalFilters} />
          <WfhQueue filters={approvalFilters} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {pageHeader}

      {data && data.unlinked && data.unlinked.length > 0 && (
        <UnlinkedMembersBanner unlinked={data.unlinked} canManage={canOverride} onChanged={load} />
      )}

      {data && data.ignored_count > 0 && (
        <p data-testid="ignored-count" className="text-xs text-gray-500">
          {data.ignored_count} Slack member{data.ignored_count === 1 ? '' : 's'} deliberately untracked.
          {canOverride && (
            <Link to="/attendance/ignored" className="ml-1 underline">Review</Link>
          )}
        </p>
      )}

      <CFiltersBar
        searchTerm={filters.search}
        onSearchChange={(e) => setFilterValue('search', e.target.value)}
        searchPlaceholder="Search employee..."
        searchTestId="attendance-search"
        searchLoading={loading && Boolean(data)}
        searchSuggestions={suggestions}
        onSearchSuggestionSelect={(name) => {
          setFilterValue('search', name);
          // A picked name is a finished thought: no reason to wait out the debounce.
          setDebouncedSearch(name);
        }}
        // Five fields in one row only once there is room: with the sidebar open,
        // a five-column row at tablet width cut the month and department short.
        gridColsClass={canReadAll ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5' : 'sm:grid-cols-2 lg:grid-cols-4'}
        onClear={clearFilters}
        clearTestId="attendance-clear-filters"
        filters={[
          {
            key: 'month',
            label: 'Month',
            value: filters.month,
            onChange: setFilter('month'),
            options: months,
          },
          ...(canReadAll ? [{
            key: 'department',
            label: 'Department',
            multiple: true,
            testId: 'attendance-department-filter',
            placeholder: 'All departments',
            value: filters.departmentIds,
            onChange: (ids) => setFilterValue('departmentIds', ids),
            options: departments.map((d) => ({ value: String(d.id), label: d.name })),
          }] : []),
          {
            key: 'employmentType',
            label: 'Employment type',
            value: filters.employmentType,
            onChange: setFilter('employmentType'),
            options: [
              { value: '', label: 'All types' },
              ...EMPLOYMENT_TYPES.map((t) => ({ value: t, label: t })),
            ],
          },
        ]}
      />

      <div className="rounded-lg border border-gray-200 bg-white">
        {error && <p className="p-6 text-sm text-red-700">{error}</p>}
        {!error && firstLoad && <p className="p-6 text-sm text-gray-500">Loading...</p>}
        {!error && !firstLoad && rows.length === 0 && (
          <div className="p-10 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
            <p className="mt-2 text-sm text-gray-500">
              {isFiltered
                ? 'No one matches these filters.'
                : 'No attendance recorded for this month.'}
            </p>
          </div>
        )}
        {!error && !firstLoad && rows.length > 0 && (
          // The matrix scrolls inside its own container. The page body never
          // scrolls horizontally.
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 top-0 z-20 border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left font-medium text-gray-500"
                  >
                    Name
                  </th>
                  {dates.map((d) => (
                    <th
                      key={d}
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-3 py-3 text-center font-medium text-gray-500 whitespace-nowrap"
                    >
                      <span className="block">{d.slice(8)}</span>
                      <span className="block text-xs font-normal text-gray-400">
                        {new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.employee_id}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-b border-r border-gray-200 bg-white px-4 py-2 text-left font-normal text-gray-900 whitespace-nowrap"
                    >
                      {row.name}
                      <span className="ml-2 text-xs text-gray-400">{row.employment_type}</span>
                    </th>
                    {dates.map((d) => {
                      const cell = row.cells[d];
                      if (!cell) {
                        return <td key={d} className="border-b border-gray-100 px-3 py-2" />;
                      }
                      return (
                        <td key={d} className="border-b border-gray-100 px-2 py-2 text-center">
                          <button
                            type="button"
                            data-testid={`cell-${row.employee_id}-${d}`}
                            onClick={() => canOverride && setEditing({
                              ...cell, employeeName: row.name, employeeId: row.employee_id, date: d,
                            })}
                            disabled={!canOverride}
                            title={cellTooltip(cell)}
                            className={`inline-flex min-w-[4.5rem] justify-center rounded px-2 py-1 text-xs font-medium ${cellClass(cell.cell)} ${canOverride ? 'cursor-pointer hover:ring-2 hover:ring-gray-300' : 'cursor-default'}`}
                          >
                            {cell.cell}
                            {cell.overridden && <span className="ml-1" aria-label="corrected">*</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {policyOpen && (
        <AttendancePolicyModal
          onClose={() => setPolicyOpen(false)}
          onSaved={load}
        />
      )}

      {editing && (
        <AttendanceOverrideModal
          cell={editing}
          timeZone={zone}
          canViewDay={canViewApprovals}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default Attendance;
